import { useCallback, useEffect, useRef, useState } from 'react';
import { startPointerDrag } from '../Utils/pointerDrag';

const STORAGE_PREFIX = 'mdv-panel-size:';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function readStored(id) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number') {
      return { width: parsed.width, height: parsed.height };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeStored(id, size) {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(size));
  } catch {
    /* ignore */
  }
}

/**
 * Persistable panel size with edge-drag handlers.
 * @param {string} id
 * @param {{ width: number, height: number } | (() => { width: number, height: number })} getDefault
 * @param {{ minW?: number, minH?: number, maxW?: number, maxH?: number }} limits
 */
export function usePanelSize(id, getDefault, limits = {}) {
  const minW = limits.minW ?? 240;
  const minH = limits.minH ?? 180;
  const active = Boolean(id);

  const resolveDefault = useCallback(() => {
    try {
      return typeof getDefault === 'function' ? getDefault() : getDefault;
    } catch {
      return { width: 320, height: 480 };
    }
  }, [getDefault]);

  const [size, setSize] = useState(() => {
    if (!active) return resolveDefault();
    const stored = readStored(id);
    if (stored) return stored;
    return resolveDefault();
  });

  const resizeRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const keepInBounds = () => {
      setSize((prev) => {
        const maxW = limits.maxW ?? Math.max(minW, window.innerWidth - 24);
        const maxH = limits.maxH ?? Math.max(minH, window.innerHeight - 48);
        const next = {
          width: clamp(prev.width, minW, maxW),
          height: clamp(prev.height, minH, maxH),
        };
        if (next.width === prev.width && next.height === prev.height) return prev;
        writeStored(id, next);
        return next;
      });
    };
    keepInBounds();
    window.addEventListener('resize', keepInBounds);
    return () => window.removeEventListener('resize', keepInBounds);
  }, [id, active, minW, minH, limits.maxW, limits.maxH]);

  const setSizeAndStore = useCallback(
    (next) => {
      setSize((prev) => {
        const maxW = limits.maxW ?? Math.max(minW, typeof window !== 'undefined' ? window.innerWidth - 24 : 1920);
        const maxH = limits.maxH ?? Math.max(minH, typeof window !== 'undefined' ? window.innerHeight - 48 : 1080);
        const resolved = typeof next === 'function' ? next(prev) : next;
        const clamped = {
          width: clamp(resolved.width, minW, maxW),
          height: clamp(resolved.height, minH, maxH),
        };
        if (active) writeStored(id, clamped);
        return clamped;
      });
    },
    [id, active, minW, minH, limits.maxW, limits.maxH]
  );

  /**
   * @param {'e'|'s'|'se'|'w'|'n'|'ne'|'sw'|'nw'} edge
   */
  const onResizePointerDown = useCallback(
    (edge) => (e) => {
      if (!active) return;
      if (e.button != null && e.button !== 0) return;
      const startX = e.clientX;
      const startY = e.clientY;
      resizeRef.current = { startX, startY, origin: { ...size }, edge };

      startPointerDrag(e, {
        onMove: (ev) => {
          if (!resizeRef.current) return;
          const { startX: sx, startY: sy, origin, edge: ed } = resizeRef.current;
          const dx = ev.clientX - sx;
          const dy = ev.clientY - sy;
          const maxW = limits.maxW ?? Math.max(minW, window.innerWidth - 24);
          const maxH = limits.maxH ?? Math.max(minH, window.innerHeight - 48);

          let width = origin.width;
          let height = origin.height;

          if (ed.includes('e')) width = origin.width + dx;
          if (ed.includes('w')) width = origin.width - dx;
          if (ed.includes('s')) height = origin.height + dy;
          if (ed.includes('n')) height = origin.height - dy;

          setSize({
            width: clamp(width, minW, maxW),
            height: clamp(height, minH, maxH),
          });
        },
        onEnd: () => {
          resizeRef.current = null;
          setSize((current) => {
            if (active) writeStored(id, current);
            return current;
          });
        },
      });
    },
    [id, active, size, minW, minH, limits.maxW, limits.maxH]
  );

  return { size, setSize: setSizeAndStore, onResizePointerDown };
}
